import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import {
  MAT_DIALOG_DATA, MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { Team } from "../../../common/models/team";
import { Lang } from "../../../common/models/lang";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthleteRow, AthletesProvider } from "../../providers/athletes-provider";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { User } from "../../../common/models/user";
import { Athlete } from "../../../common/models/athlete";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatFormField } from "@angular/material/form-field";
import { MatOption, MatSelect } from "@angular/material/select";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatProgressBar } from "@angular/material/progress-bar";
import { Gender } from "../../../common/models/gender";
import { ContractType } from "../../../common/models/contract-type";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { throws } from "node:assert";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { take } from "rxjs/operators";

@Component({
  selector: 'athletes-teams-form',
  providers: [],
  styleUrls: ['./teams.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatTooltip,
    MatIcon,
    MatDialogContent,
    MatFormField,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    NgForOf,
    MatCheckbox,
    FormsModule,
    MatDialogActions,
    MatButton,
    MatProgressBar,
    NgIf,
    AsyncPipe,
    NgxMatSelectSearchModule
  ],
  templateUrl: './teams.html'
})
export class AthletesTeamsFormComponent implements AfterViewInit, OnInit, OnDestroy {
  public isLoading = false;
  public formTitle = '';
  public confirmButtonTitle = 'Save';
  public organisation: Organization | null = null;
  public athleteId = 0;
  public userId = 0;
  public langId: number = Lang.ID_DEFAULT;
  public removeCoachAthleteRelations = true;
  public removeGuestAthleteRelations = true;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;

  public availableTeams: Team[] = [];
  public teamsSelected: Team[] = [];
  public teamsSelectFormControl: FormControl<Team[]> = new FormControl<Team[]>([]) as FormControl<Team[]>;
  public teamFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredTeams: ReplaySubject<Team[]> = new ReplaySubject<Team[]>(1);
  protected _onDestroy = new Subject<void>();

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<AthletesTeamsFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _athletesProvider: AthletesProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    @Inject(MAT_DIALOG_DATA) private _data: any
  ) {
    this.availableTeams = this._data.availableTeams;
    this.organisation = _data.organization;

    if (this._data.athleteRow) {

      this.athleteId = this._data.athleteRow.athlete.id;
      this.userId = this._data.athleteRow.user.id;
      this.langId = this._data.athleteRow.user.langId;

      this.teamsSelected = this._data.athleteRow.teams;
    }

    this.formTitle = this._data.formTitle;
    this.confirmButtonTitle = this._data.confirmButtonTitle;
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredTeams.next(this.availableTeams.slice());

    // Écouter les changements dans le champ de recherche
    this.teamFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterTeams();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.teamsSelected.length > 0) {
      this.teamsSelectFormControl.setValue(this.teamsSelected);
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public onSaveClicked(): void {
    if (this.isValid()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: AthleteRow) => {
          this._toaster.open(this._translater.instant('Athlete_saved'), '', { duration: 5000 });
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      })
    } else {
      this.touchStepFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
    }
  }

  private isValid(): boolean {
    return !this.teamsSelectFormControl.invalid;
  }

  private touchStepFields(): void {
    this.teamsSelectFormControl.markAsTouched();
  }

  private save(): Observable<AthleteRow> {
    const athleteRow: AthleteRow = {
      user: new User({}),
      athlete: new Athlete({}),
      gender: {} as Gender,
      contractType: {} as ContractType,
      addresses: [],
      coaches: [],
      coachNames: '',
      teams: [],
      teamNames: '',
      rights: undefined,
    };

    athleteRow.athlete.id = this.athleteId;
    athleteRow.user.id = this.userId;

    // team
    if (this.teamsSelectFormControl.value.length) {
      for (let i = 0; i < this.teamsSelectFormControl.value.length; i++) {

        const team = this.utils.findIn(
          this.teamsSelectFormControl.value[i].id,
          this.availableTeams,
          'id');
        if (team) {
          athleteRow.teams.push(team);
        }
      }
    }
    if (!this.organisation?.id) throw('Organisation id is missing');
    return this._athletesProvider.saveAthleteTeams(this.organisation.id, this.removeCoachAthleteRelations, this.removeGuestAthleteRelations, athleteRow)
  }

  protected setInitialValue() {
    this.filteredTeams
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: Team, b: Team) => a && b && a.id === b.id;
      });
  }

  protected filterTeams() {
    if (!this.availableTeams) {
      return;
    }

    const search = this.teamFilterFormControl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredTeams.next(this.availableTeams.slice());
      return;
    }

    this.filteredTeams.next(
      this.availableTeams.filter((team) =>
        `${team.name}`.toLowerCase().includes(search)
      )
    );
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple
        // (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
  }

}
